import { useMemo } from "react";
import { useGetAccountHistoryQuery } from "features/trading/accounts";
import {
  AccountHistoryRecord,
  BalanceTypeEnum,
  OptionTypeEnum,
} from "helpers/types";

const RECENT_LIMIT = 2;

export interface AccountHistoryStatus {
  hasBoth: boolean;
  recentByType: Record<BalanceTypeEnum, AccountHistoryRecord[]>;
  isLoading: boolean;
}

export const useAccountHistoryStatus = (
  accountId: number | null,
  assetId: number | null,
  optionType: OptionTypeEnum | null,
): AccountHistoryStatus => {
  const { data, isLoading } = useGetAccountHistoryQuery(
    {
      account_id: accountId as number,
      asset_id: assetId as number,
      ordering: "-adjustment_date,-created_on",
    },
    { skip: !accountId || !assetId },
  );

  const history = data ?? [];

  return useMemo(() => {
    const recentByType: Record<BalanceTypeEnum, AccountHistoryRecord[]> = {
      // TRADE_AMOUNT rows are per-letter, so filter by option_type if provided.
      // PORTFOLIO rows carry no letter — always include them for the given asset.
      [BalanceTypeEnum.TRADE_AMOUNT]: history
        .filter(
          (record) =>
            record.balance_type === BalanceTypeEnum.TRADE_AMOUNT &&
            (!optionType || record.option_type === optionType),
        )
        .slice(0, RECENT_LIMIT),
      [BalanceTypeEnum.PORTFOLIO]: history
        .filter((record) => record.balance_type === BalanceTypeEnum.PORTFOLIO)
        .slice(0, RECENT_LIMIT),
    };

    return {
      hasBoth:
        recentByType[BalanceTypeEnum.TRADE_AMOUNT].length > 0 &&
        recentByType[BalanceTypeEnum.PORTFOLIO].length > 0,
      recentByType,
      isLoading,
    };
  }, [history, isLoading, optionType]);
};
